import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type {
  ActivityProgressProps,
  ActivityProgressSlot,
  LegendProps,
  ProgressSlotProps,
} from './interfaces';
import { theme } from '@/@mui';
import { activityStatusLabel } from '@/constants/idp/activity-status';
import { ActivityStatus } from '@/data/idp/plans/interfaces';

const activityStatusMainColorMap = {
  [ActivityStatus.Completed]: theme.palette.success.main,
  [ActivityStatus.InProgress]: theme.palette.info.main,
  [ActivityStatus.Pending]: theme.palette.status.todo,
};

const ActivityProgressSlot = ({
  backgroundColor,
  id,
  label,
  percentage,
}: ProgressSlotProps) => (
  <Tooltip title={label} placement="top">
    <Box
      component="span"
      data-testid={`activity-progress-${id}`}
      sx={{
        backgroundColor,
        overflow: 'hidden',
        textIndent: '100%',
        whiteSpace: 'nowrap',
        width: `${percentage}%`,
      }}
    >
      {percentage}
    </Box>
  </Tooltip>
);

const ActivityProgressLegend = ({ backgroundColor, label }: LegendProps) => (
  <Stack direction="row" alignItems="center" gap={1}>
    <Box
      sx={(theme) => ({
        backgroundColor,
        height: theme.spacing(2),
        width: theme.spacing(2),
        borderRadius: theme.spacing(2),
      })}
    />
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

const formatDataSlot = (key: ActivityStatus, item: ActivityProgressSlot) => {
  const { percentage = 0, quantity } = item;
  const backgroundColor = activityStatusMainColorMap[key];
  const label = `${activityStatusLabel[key]}: ${quantity} (${percentage.toFixed(0)}%)`;

  return {
    backgroundColor,
    label,
    percentage,
    quantity,
  };
};

export const ActivityProgress = ({
  data,
  legend = true,
  card,
}: ActivityProgressProps) => {
  const formattedData = Object.entries(data).map(([key, item]) => ({
    key,
    ...formatDataSlot(key as ActivityStatus, item),
  }));

  return (
    <Box>
      <Typography variant={card ? 'h3' : 'h2'} fontWeight="semibold">
        Progresso {data[ActivityStatus.Completed].percentage?.toFixed(0) || 0}%
      </Typography>
      <Stack
        direction="row"
        sx={{
          backgroundColor: 'neutral.500',
          height: ({ spacing }) => spacing(1),
          width: '100%',
          mt: ({ spacing }) => spacing(2),
        }}
      >
        {formattedData.map(({ key, backgroundColor, label, percentage }) => (
          <ActivityProgressSlot
            key={key}
            backgroundColor={backgroundColor}
            id={key}
            label={label}
            percentage={percentage}
          />
        ))}
      </Stack>
      {legend && (
        <Stack spacing={1} mt={2}>
          {formattedData.map(({ key, backgroundColor, label }) => (
            <ActivityProgressLegend
              key={key}
              backgroundColor={backgroundColor}
              label={label}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
